import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@mlm/ui';
import {
  createCustomer,
  getCustomer,
  listCustomerDocuments,
  listCustomers,
  listPaymentTerms,
  setCustomerActive,
  updateCustomer,
  type CustomerInput,
  type CustomerKind,
} from '../lib/api';

function errText(error: unknown): string | undefined {
  return (error as { response?: { data?: { error?: string } } } | null)?.response?.data?.error;
}

const ERROR_COPY: Record<string, string> = {
  member_requires_user: 'A member customer needs the member it is bridged to.',
  user_requires_member_kind: 'Only a “member” customer can be linked to a member account.',
  name_required: 'Name is required.',
  customer_not_found: 'That customer no longer exists.',
  user_not_found: 'That member account no longer exists.',
};
const friendly = (e: unknown) => {
  const code = errText(e);
  return code ? (ERROR_COPY[code] ?? code) : undefined;
};

const KIND_LABEL: Record<CustomerKind, string> = {
  member: 'Member',
  individual: 'Individual',
  business: 'Business',
};

const SELECT_CLS = 'h-9 w-full rounded-md border border-input bg-background px-2 text-sm shadow-sm';

/**
 * Customers (Accounting → Customers).
 *
 * The R2 customer master: who the company bills. A MEMBER customer is bridged
 * automatically the first time they are consigned to — those rows are created
 * by the system, not here, which is why `kind` is fixed at creation and the
 * member link is never editable. Individuals and businesses are created here.
 */
export function CustomersPage() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['customers', q, showInactive],
    queryFn: () => listCustomers({ q: q || undefined, active: showInactive ? undefined : true }),
  });
  const termsQuery = useQuery({ queryKey: ['payment-terms'], queryFn: () => listPaymentTerms() });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['customers'] });
  const create = useMutation({
    mutationFn: createCustomer,
    meta: { successMessage: 'Customer created.' },
    onSuccess: () => {
      invalidate();
      setCreating(false);
    },
  });
  const toggleActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => setCustomerActive(id, active),
    meta: { successMessage: 'Customer updated.' },
    onSuccess: invalidate,
  });

  const rows = query.data?.rows ?? [];
  const terms = termsQuery.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold">Customers</h1>
        <Button onClick={() => setCreating(true)}>+ New customer</Button>
      </div>
      <p className="text-sm text-muted-foreground">
        Who the company bills. Members are bridged automatically the first time you consign to them;
        add individuals and businesses here.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, code, email or phone…"
          className="max-w-sm"
        />
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Show inactive
        </label>
      </div>

      {creating && (
        <CustomerForm
          terms={terms.map((t) => ({ code: t.code, name: t.name }))}
          submitting={create.isPending}
          error={friendly(create.error)}
          onSubmit={(input) => create.mutate(input)}
          onCancel={() => setCreating(false)}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle>
            {query.data
              ? `${query.data.total} customer${query.data.total === 1 ? '' : 's'}`
              : 'Customers'}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {query.isLoading ? (
            <p className="p-4 text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">
              {q ? 'No customers match that search.' : 'No customers yet.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b bg-muted/40 text-left">
                  <tr>
                    <th className="px-4 py-2 font-medium">Code</th>
                    <th className="px-4 py-2 font-medium">Name</th>
                    <th className="px-4 py-2 font-medium">Type</th>
                    <th className="px-4 py-2 font-medium">Terms</th>
                    <th className="px-4 py-2 text-right font-medium">Credit limit</th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {rows.map((c) => (
                    <tr key={c.id} className={c.active ? '' : 'opacity-60'}>
                      <td className="whitespace-nowrap px-4 py-2 font-mono text-xs">{c.code}</td>
                      <td className="px-4 py-2">
                        <button
                          className="text-left hover:underline"
                          onClick={() => setSelected(c.id)}
                        >
                          {c.name}
                        </button>
                        {!c.active && (
                          <span className="ml-2 text-xs text-muted-foreground">(inactive)</span>
                        )}
                      </td>
                      <td className="px-4 py-2">{KIND_LABEL[c.kind] ?? c.kind}</td>
                      <td className="px-4 py-2">{c.termsCode ?? '—'}</td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {Number(c.creditLimit) > 0 ? Number(c.creditLimit).toLocaleString() : '—'}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={toggleActive.isPending}
                          onClick={() => toggleActive.mutate({ id: c.id, active: !c.active })}
                        >
                          {c.active ? 'Deactivate' : 'Reactivate'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {selected && (
        <CustomerDetailPanel
          id={selected}
          terms={terms.map((t) => ({ code: t.code, name: t.name }))}
          onClose={() => setSelected(null)}
          onSaved={invalidate}
        />
      )}
    </div>
  );
}

function CustomerForm({
  terms,
  initial,
  submitting,
  error,
  onSubmit,
  onCancel,
}: {
  terms: Array<{ code: string; name: string }>;
  initial?: Partial<CustomerInput>;
  submitting: boolean;
  error?: string;
  onSubmit: (input: CustomerInput) => void;
  onCancel: () => void;
}) {
  const [kind, setKind] = useState<CustomerKind>((initial?.kind as CustomerKind) ?? 'individual');
  const [name, setName] = useState(initial?.name ?? '');
  const [tradeName, setTradeName] = useState(initial?.tradeName ?? '');
  const [tin, setTin] = useState(initial?.tin ?? '');
  const [vatRegistered, setVatRegistered] = useState(initial?.vatRegistered ?? false);
  const [email, setEmail] = useState(initial?.email ?? '');
  const [phone, setPhone] = useState(initial?.phone ?? '');
  const [termsCode, setTermsCode] = useState(initial?.termsCode ?? '');
  const [creditLimit, setCreditLimit] = useState(initial?.creditLimit ?? '');

  return (
    <Card>
      <CardHeader>
        <CardTitle>{initial ? 'Edit customer' : 'New customer'}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          {!initial && (
            <Field label="Type">
              <select
                className={SELECT_CLS}
                value={kind}
                onChange={(e) => setKind(e.target.value as CustomerKind)}
              >
                {/* `member` is intentionally absent: those rows are created by
                    the bridge when a member is first consigned to, never here. */}
                <option value="individual">Individual</option>
                <option value="business">Business</option>
              </select>
            </Field>
          )}
          <Field label="Name">
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Trade name (optional)">
            <Input value={tradeName ?? ''} onChange={(e) => setTradeName(e.target.value)} />
          </Field>
          <Field label="TIN">
            <Input value={tin ?? ''} onChange={(e) => setTin(e.target.value)} />
          </Field>
          <Field label="Email">
            <Input value={email ?? ''} onChange={(e) => setEmail(e.target.value)} />
          </Field>
          <Field label="Phone">
            <Input value={phone ?? ''} onChange={(e) => setPhone(e.target.value)} />
          </Field>
          <Field label="Payment terms">
            <select
              className={SELECT_CLS}
              value={termsCode ?? ''}
              onChange={(e) => setTermsCode(e.target.value)}
            >
              <option value="">Use the tenant default</option>
              {terms.map((t) => (
                <option key={t.code} value={t.code}>
                  {t.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Credit limit (0 = no limit)">
            <Input
              inputMode="decimal"
              value={creditLimit ?? ''}
              onChange={(e) => setCreditLimit(e.target.value)}
            />
          </Field>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={vatRegistered}
            onChange={(e) => setVatRegistered(e.target.checked)}
          />
          VAT-registered
        </label>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex gap-2">
          <Button
            disabled={submitting || !name.trim()}
            onClick={() =>
              onSubmit({
                kind,
                name: name.trim(),
                tradeName: tradeName || null,
                tin: tin || null,
                vatRegistered,
                email: email || null,
                phone: phone || null,
                termsCode: termsCode || null,
                creditLimit: creditLimit || null,
              })
            }
          >
            {submitting ? 'Saving…' : 'Save'}
          </Button>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function CustomerDetailPanel({
  id,
  terms,
  onClose,
  onSaved,
}: {
  id: string;
  terms: Array<{ code: string; name: string }>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const detail = useQuery({ queryKey: ['customer', id], queryFn: () => getCustomer(id) });
  const docs = useQuery({
    queryKey: ['customer-docs', id],
    queryFn: () => listCustomerDocuments(id),
  });
  const save = useMutation({
    mutationFn: (patch: Partial<CustomerInput>) => updateCustomer(id, patch),
    meta: { successMessage: 'Customer saved.' },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customer', id] });
      onSaved();
      setEditing(false);
    },
  });

  const c = detail.data;

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between">
        <CardTitle>{c ? `${c.name} · ${c.code}` : 'Customer'}</CardTitle>
        <Button variant="ghost" size="sm" onClick={onClose}>
          Close
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {detail.isLoading || !c ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : editing ? (
          <CustomerForm
            terms={terms}
            initial={c}
            submitting={save.isPending}
            error={friendly(save.error)}
            onSubmit={(input) => {
              const { kind: _kind, userId: _userId, ...patch } = input;
              save.mutate(patch);
            }}
            onCancel={() => setEditing(false)}
          />
        ) : (
          <>
            <div className="grid gap-3 text-sm sm:grid-cols-3">
              <Stat label="Type" value={KIND_LABEL[c.kind] ?? c.kind} />
              <Stat label="Open billings" value={String(c.openInvoiceCount)} />
              <Stat label="Outstanding" value={Number(c.outstanding).toLocaleString()} />
              <Stat label="Terms" value={c.termsCode ?? 'Tenant default'} />
              <Stat label="TIN" value={c.tin ?? '—'} />
              <Stat label="VAT-registered" value={c.vatRegistered ? 'Yes' : 'No'} />
            </div>
            <Button size="sm" onClick={() => setEditing(true)}>
              Edit
            </Button>
          </>
        )}

        <div>
          <h3 className="mb-2 text-sm font-medium">Documents</h3>
          {docs.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (docs.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing billed to this customer yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b text-left">
                  <tr>
                    <th className="py-1 font-medium">Document</th>
                    <th className="py-1 font-medium">Date</th>
                    <th className="py-1 font-medium">Status</th>
                    <th className="py-1 text-right font-medium">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {(docs.data ?? []).map((d) => (
                    <tr key={`${d.kind}-${d.docNo}`}>
                      <td className="py-1 font-mono text-xs">{d.docNo}</td>
                      <td className="py-1">{new Date(d.docDate).toLocaleDateString()}</td>
                      <td className="py-1">{d.status}</td>
                      <td className="py-1 text-right tabular-nums">
                        {d.amount === null ? '—' : Number(d.amount).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  );
}
